import { arenaSizeFor, defaultConfig } from './engine.js';
import type { ArenaEventId, GameConfig, GameMode, GameRules } from './types.js';

/**
 * Multiplayer rule sets on top of FFA/teams.
 * - classic:  the original game, no extras
 * - powerups: ghost, double points, magnet, fire and more pickups spawn on the board
 * - royale:   power-ups plus a zone that keeps closing in; last snake standing wins
 * - endless:  a huge scrolling arena (slither.io style): respawn after dying, spawn
 *             protection, lots of power-ups (30 s ghost, wide magnet, electric shock, fire),
 *             and a score race
 * - koth:     king of the hill: hold the (moving) hill alone to score; respawn, 3 minutes
 * - ctf:      capture the food (teams only): carry the trophy from the middle into your base
 */
export type MatchVariant = 'classic' | 'powerups' | 'royale' | 'endless' | 'koth' | 'ctf';
export const MATCH_VARIANTS: MatchVariant[] = ['endless', 'classic', 'powerups', 'royale', 'koth', 'ctf'];

/** Variants that only make sense with two teams. */
export const TEAM_ONLY_VARIANTS: readonly MatchVariant[] = ['ctf'];

/** Respawn modes played on the clock (not last snake standing). */
const TIMED_TICKS: Partial<Record<MatchVariant, number>> = { koth: 12 * 180, ctf: 12 * 240 };

export function variantRules(v: MatchVariant): GameRules | undefined {
  if (v === 'powerups') return { powerups: true, spawnShield: true, fire: true };
  if (v === 'royale') return { powerups: true, shrink: true, spawnShield: true, fire: true };
  if (v === 'endless') {
    return {
      powerups: true,
      respawn: true,
      spawnShield: true,
      pointScale: 100,
      boost: true,
      // A huge map needs many more pickups than a small one to feel alive.
      maxPowerups: 12,
      powerupChance: 1 / 12,
      ghostTicks: 12 * 30,
      magnetRadius: 3,
      electric: true,
      fire: true,
      botHandicap: { pointShare: 0.5, maxLength: 70 },
    };
  }
  if (v === 'koth') return { powerups: true, respawn: true, spawnShield: true, hill: true, boost: true, fire: true };
  if (v === 'ctf') return { respawn: true, spawnShield: true, ctf: true };
  return undefined;
}

/** Lives a player can pick for the big arena: three hearts, or one life (sudden death). */
export type ArenaLives = 1 | 3;
export const ARENA_LIVES: ArenaLives[] = [3, 1];

/** Big arenas are topped up with bots to this many snakes so they never feel empty. */
export const ARENA_TARGET_PLAYERS = 12;
/** Seats in a persistent quick-play arena (humans + bots). A full arena spills into a new one. */
export const ARENA_SLOTS = 24;
/**
 * The big arena runs at 10 ticks a second (other modes 12): with its zoomed-in view a snake crosses the
 * screen faster, and at 12 it felt rushed. Everything counted in ticks there (power-ups, respawns) lasts
 * a little longer in seconds to match.
 */
export const ARENA_TICK_RATE = 10;
export const ARENA_DURATION_TICKS = ARENA_TICK_RATE * 60 * 10;

/** Side of the big arena, growing with the snake count (every mode uses the same size). */
export const arenaSide = (players: number) => arenaSizeFor(players).width;

export function configFor(variant: MatchVariant, players: number, overrides: Partial<GameConfig> & { mode?: GameMode } = {}): GameConfig {
  const rules = variantRules(variant);
  const timed = TIMED_TICKS[variant];
  if (timed) {
    const base = defaultConfig(players, {});
    // Capture the food plays on a wide field: one base on each side, the trophy in the middle.
    const width = variant === 'ctf' ? base.width + Math.round(base.width / 4) : base.width;
    return defaultConfig(players, { width, height: base.height, durationTicks: timed, foodTarget: Math.round((width * base.height) / 45), ...overrides, rules });
  }
  if (variant !== 'endless') return defaultConfig(players, { ...overrides, rules });
  const side = arenaSide(players);
  return defaultConfig(players, {
    width: side,
    height: side,
    tickRate: ARENA_TICK_RATE,
    // The usual 3 s countdown (36 ticks at 12 Hz would stretch it to 3.6 s).
    countdownTicks: 3 * ARENA_TICK_RATE,
    durationTicks: ARENA_DURATION_TICKS,
    // Roughly one piece per 90 cells: enough to chase, with room to see the snakes and plan a route.
    foodTarget: Math.round((side * side) / 90),
    ...overrides,
    rules,
  });
}

/** One event per week, in this order, switching every Monday 00:00 UTC. */
export const ARENA_EVENTS: readonly ArenaEventId[] = ['feast', 'frenzy', 'giants'];
export const ARENA_EVENT_ICON: Record<ArenaEventId, string> = { feast: '🍎', frenzy: '⚡', giants: '🐍' };
const WEEK_MS = 7 * 24 * 3600 * 1000;
/** A Monday, 00:00 UTC. */
const EVENT_EPOCH = Date.UTC(2024, 0, 1);

/** This week's big-arena event at time `ms`, and when it ends. */
export function arenaEventAt(ms: number): { id: ArenaEventId; endsAt: number } {
  const week = Math.floor((ms - EVENT_EPOCH) / WEEK_MS);
  const n = ARENA_EVENTS.length;
  return { id: ARENA_EVENTS[((week % n) + n) % n]!, endsAt: EVENT_EPOCH + (week + 1) * WEEK_MS };
}

/** A big-arena config with an event's changes applied. */
export function withArenaEvent(c: GameConfig, id: ArenaEventId): GameConfig {
  const rules = { ...c.rules, event: id };
  if (id === 'feast') return { ...c, foodTarget: c.foodTarget * 2, rules };
  if (id === 'frenzy') return { ...c, rules: { ...rules, maxPowerups: (c.rules?.maxPowerups ?? 2) * 2, powerupChance: (c.rules?.powerupChance ?? 1 / 70) * 2 } };
  return { ...c, startLength: c.startLength * 3, rules };
}

const BOT_NAMES = ['Viper', 'Cobra', 'Mamba', 'Python', 'Boa', 'Krait', 'Taipan', 'Adder', 'Racer', 'Noodle', 'Slinky', 'Fang', 'Hiss', 'Zigzag', 'Sidewinder'];
/** Bots are always marked as bots (🤖 in their name and `isBot` in results). */
export const botName = (i: number) => `🤖 ${BOT_NAMES[i % BOT_NAMES.length]}`;
